import { useEffect, useState } from 'react';
import { defensePlansApi } from '../api/defensePlans';
import { DEFENSE_STRATEGIES } from '../components/map/defenseStrategies';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('usePublicPlanBundle');

export type PublicPlanBundle =
  | { status: 'loading' }
  /** Private, missing, gone, or not open for bids. The page says it plainly
   *  and never says which, so a link can't tell anyone a private plan exists. */
  | { status: 'closed' }
  | { status: 'error' }
  | {
      status: 'ready';
      planId: string;
      bundleId: string;
      planTitle: string;
      ownerName: string;
      bundleName: string;
      /** "3 homes" or "5 defenses", the way the plan panel counts a bundle. */
      countLabel: string;
      /** Each kind of work once, by its label: "Ember-resistant vents". */
      strategies: string[];
      /** Street addresses of the homes in the bundle, where the plan has them. */
      addresses: string[];
    };

/**
 * Everything the public /opportunity page shows, for a vendor with no
 * account: the bundle's work, read once from the shared plan. Rules let
 * anyone read a shared plan; a private one is denied and reads as closed.
 */
export function usePublicPlanBundle(
  planId: string | undefined,
  bundleId: string | undefined
): PublicPlanBundle {
  const [state, setState] = useState<PublicPlanBundle>({ status: 'loading' });

  useEffect(() => {
    if (!planId || !bundleId) {
      setState({ status: 'closed' });
      return;
    }
    setState({ status: 'loading' });
    let cancelled = false;
    defensePlansApi
      .getPlan(planId)
      .then((plan) => {
        if (cancelled) return;
        const bundle = plan?.bundles.find((b) => b.id === bundleId);
        if (!plan || plan.visibility !== 'shared' || !bundle || !bundle.bidsOpen) {
          setState({ status: 'closed' });
          return;
        }
        const members = plan.features.filter((f) => f.bundleId === bundleId);
        const homes = new Set(members.filter((m) => !!m.structureId).map((m) => m.structureId));
        const allHomes = members.length > 0 && members.every((m) => !!m.structureId);
        const countLabel = allHomes
          ? `${homes.size} ${homes.size === 1 ? 'home' : 'homes'}`
          : `${members.length} ${members.length === 1 ? 'defense' : 'defenses'}`;
        setState({
          status: 'ready',
          planId,
          bundleId,
          planTitle: plan.title,
          ownerName: plan.ownerName,
          bundleName: bundle.name,
          countLabel,
          strategies: Array.from(new Set(members.map((m) => DEFENSE_STRATEGIES[m.strategy].label))),
          addresses: Array.from(
            new Set(members.map((m) => m.address?.trim()).filter((a): a is string => !!a))
          ),
        });
      })
      .catch((error) => {
        if (cancelled) return;
        // A private plan is denied by design, and a missing doc can read as
        // denied before the rules see it.
        if ((error as { code?: string })?.code === 'permission-denied') {
          setState({ status: 'closed' });
          return;
        }
        log.warn('Could not load the opportunity:', error);
        setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [planId, bundleId]);

  return state;
}
